/**
 * Theme Gallery Component
 * 
 * Displays available themes loaded from themes.yaml and allows users to preview
 * and switch between them. Respects admin-configured default theme while allowing
 * user preferences.
 * 
 * AUTHORIZATION: Uses PDP policies to control who can change themes
 * - Resource: 'experience:theme-picker'
 * - Action: 'change-theme'
 * 
 * @module components/ThemeGallery
 * @version 1.0.0
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Palette } from 'lucide-react';
import { useTheme, Theme } from '../contexts/ThemeContext';
import { useBrandingContext } from '../contexts/BrandingContext';
import { useAuthorization } from '../core/auth/authz-hooks';
import './ThemeGallery.css';

interface ThemeDefinition {
  id: string;
  name: string;
  description: string;
  colors: Record<string, string>;
  isOfficial?: boolean;
  isCustom?: boolean;
}

function resolveThemeDisplayName(themeId: string | undefined, themes: ThemeDefinition[]): string {
  if (!themeId) return 'Default';
  const match = themes.find((t) => t.id === themeId);
  if (match?.name) return match.name;
  if (themeId.toLowerCase() === 'empowerid') return 'EmpowerID';
  return themeId
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/**
 * Theme Gallery Modal Component
 * Shows all available themes from themes.yaml plus built-in themes
 */
export function ThemeGallery({ onClose }: { onClose?: () => void }) {
  const { theme, setTheme, adminDefault, isUserChoice, resetToDefault } = useTheme();
  const { config: branding } = useBrandingContext();
  const [availableThemes, setAvailableThemes] = useState<ThemeDefinition[]>([]);
  const [loading, setLoading] = useState(true);

  // Load available themes from API
  useEffect(() => {
    let isMounted = true;
    
    const load = async () => {
      await loadThemes();
      // Only update state if component is still mounted
      if (!isMounted) return;
    };
    
    load();
    
    return () => {
      isMounted = false;
    };
  }, []);

  const loadThemes = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout
      
      const response = await fetch('/api/configs/branding/themes', {
        credentials: 'include',
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const themesData = await response.json();
        
        // Convert themes object to array with validation
        const themeArray: ThemeDefinition[] = Object.entries(themesData)
          .filter(([id, def]: [string, any]) => (
            id &&
            typeof id === 'string' &&
            !id.startsWith('_') &&
            def &&
            typeof def === 'object' &&
            typeof def.name === 'string' &&
            def.name.trim().length > 0
          ))
          .map(([id, def]: [string, any]) => ({
            id,
            name: def?.name || id,
            description: def?.description || '',
            colors: def?.colors || {},
            isOfficial: id === adminDefault,  // Mark admin's configured default
          }));
        
        // If API returns themes, use them; otherwise fall back to built-ins
        setAvailableThemes(themeArray.length > 0 ? themeArray : getBuiltInThemes());
      } else {
        // Non-200 response - fall back to built-ins
        setAvailableThemes(getBuiltInThemes());
      }
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('[ThemeGallery] Failed to load themes:', error);
      }
      // Fall back to built-in themes on any error
      setAvailableThemes(getBuiltInThemes());
    } finally {
      setLoading(false);
    }
  };

  const getBuiltInThemes = (): ThemeDefinition[] => {
    // Fallback if themes API fails — CSS-backed themes always available
    return [
      {
        id: 'empowerid',
        name: 'EmpowerID',
        description: 'EmpowerID Identity Fabric — Executive Clarity (default)',
        colors: {},
      },
      {
        id: 'blue',
        name: 'NeonFlux',
        description: 'Vibrant cyberpunk blue theme',
        colors: {},
      },
      {
        id: 'white',
        name: 'Clean Light',
        description: 'Professional light theme',
        colors: {},
      },
      {
        id: 'black',
        name: 'Modern Dark',
        description: 'PayloadCMS-inspired sleek dark theme',
        colors: {},
      },
    ];
  };

  // Use adminDefault from context (fetched from branding API)
  const officialTheme = adminDefault;
  const officialThemeLabel = resolveThemeDisplayName(officialTheme, availableThemes);

  // Prevent body scroll when modal is open
  useEffect(() => {
    // Save original body overflow
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    
    return () => {
      // Restore original body overflow
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Add style override to ensure modal stays within viewport
  useEffect(() => {
    const styleId = 'theme-gallery-override';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .theme-gallery-modal {
          overflow: hidden !important;
          position: fixed !important;
          top: 20px !important;
          bottom: 20px !important;
          max-height: calc(100vh - 40px) !important;
          height: auto !important;
        }
        .theme-gallery-modal > div:nth-child(2) {
          overflow-y: auto !important;
          overflow-x: hidden !important;
          flex: 1 1 0% !important;
          min-height: 0 !important;
        }
      `;
      document.head.appendChild(style);
    }
    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  // Render modal using Portal to ensure it's at document root, not inside footer
  const modalContent = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="theme-gallery-title"
      aria-describedby="theme-gallery-description"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.7)',
        zIndex: 999999,
        overflow: 'hidden',
      }}
      onClick={onClose}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Escape' && onClose) {
          onClose();
        }
      }}
    >
      <div
        className="theme-gallery-modal"
        style={{
          maxWidth: 900,
          width: 'calc(100% - 40px)',
          position: 'fixed',
          top: '20px',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: 0,
          zIndex: 1000000,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        {/* Header - fixed, doesn't scroll */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          padding: 32,
          paddingBottom: 24,
          flexShrink: 0,
        }}>
          <div>
            <h2 id="theme-gallery-title" className="theme-gallery-modal__title-row">
              <Palette size={22} strokeWidth={1.75} aria-hidden />
              Theme Gallery
            </h2>
            <p id="theme-gallery-description" style={{ margin: '8px 0 0 0', opacity: 0.75, fontSize: 14 }}>
              Official theme: <strong>{officialThemeLabel}</strong> (set by admin)
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {/* Reset to default button - only show if user has customized */}
            {isUserChoice && theme !== adminDefault && (
              <button
                onClick={() => {
                  resetToDefault();
                }}
                aria-label="Reset to official theme"
                title={`Reset to ${adminDefault}`}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border-color)',
                  color: 'var(--main-text-secondary)',
                  padding: '8px 16px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: 500,
                  fontSize: 13,
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.currentTarget.style.borderColor = 'var(--main-accent)';
                  e.currentTarget.style.color = 'var(--main-accent)';
                }}
                onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.currentTarget.style.borderColor = 'var(--border-color)';
                  e.currentTarget.style.color = 'var(--main-text-secondary)';
                }}
              >
                ↩ Reset to Default
              </button>
            )}
            {onClose && (
              <button
                onClick={onClose}
                aria-label="Close theme gallery"
                style={{
                  background: 'var(--input-bg, rgba(255,255,255,0.1))',
                  border: '1px solid var(--border-color)',
                  color: 'var(--main-text)',
                  padding: '8px 16px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: 600,
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.currentTarget.style.background = 'var(--main-accent)';
                  e.currentTarget.style.borderColor = 'var(--main-accent)';
                  e.currentTarget.style.color = 'var(--button-text, white)';
                }}
                onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.currentTarget.style.background = 'var(--input-bg, rgba(255,255,255,0.1))';
                  e.currentTarget.style.borderColor = 'var(--border-color)';
                  e.currentTarget.style.color = 'var(--main-text)';
                }}
              >
                Close
              </button>
            )}
          </div>
        </div>

        {/* Scrollable content area */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '0 32px',
          minHeight: 0, // Critical for flex scrolling
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }} role="status" aria-live="polite">
              Loading themes...
            </div>
          ) : availableThemes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <p>No themes available. Please contact your administrator.</p>
            </div>
          ) : (
            <div
              role="list"
              aria-label="Available themes"
              className="theme-gallery-grid"
            >
              {availableThemes.map((themeOption: ThemeDefinition) => (
                <ThemeCard
                  key={themeOption.id}
                  theme={themeOption}
                  isActive={theme === themeOption.id}
                  isOfficial={themeOption.id === officialTheme}
                  onSelect={() => {
                    // Use ThemeContext instead of direct DOM manipulation
                    // ThemeContext will apply colors via ThemeManager (unified system)
                    setTheme(themeOption.id as Theme);
                    
                    if (onClose) {
                      setTimeout(onClose, 300);
                    }
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer - fixed, doesn't scroll */}
        <div className="theme-gallery-modal__footer">
          <p className="theme-gallery-modal__footer-tip">
            <strong>Tip:</strong> Theme preferences are saved per-user in your browser.
            {' '}The official theme ({officialThemeLabel}) is set by your administrator.
          </p>
        </div>
      </div>
    </div>
  );

  // Use Portal to render modal at document root
  return createPortal(modalContent, document.body);
}

/**
 * Shared theme icon/image configuration
 * Extracted to avoid duplication between ThemeCard and CompactThemeSwitcher
 */
const THEME_IMAGE_MAP: Record<string, string> = {
  sap: '/api/configs/branding/logos/sap.svg',
};

function getThemeImageUrl(themeId: string, isActive = false): string | null {
  const normalizedId = themeId.toLowerCase();
  if (normalizedId === 'empowerid') {
    return isActive
      ? '/api/configs/branding/logos/empowerid-logo-light.svg'
      : '/api/configs/branding/logos/empowerid-logo-dark.svg';
  }
  return THEME_IMAGE_MAP[normalizedId] || null;
}

const THEME_ICONS: Record<string, string> = {
  blue: '🔵',
  white: '⚪',
  black: '⚫',
  purple: '🟣',
  green: '🟢',
  orange: '🟠',
  financial: '💼',
  healthcare: '🏥',
  empowerid: '🔷',
};

function getThemeIcon(themeId: string): string {
  return THEME_ICONS[themeId] || '🎨';
}

/**
 * Theme Card Component
 * Individual theme preview card
 */
function ThemeCard({
  theme,
  isActive,
  isOfficial,
  onSelect,
}: {
  theme: ThemeDefinition;
  isActive: boolean;
  isOfficial: boolean;
  onSelect: () => void;
}) {
  const imageUrl = getThemeImageUrl(theme.id, false);
  const icon = getThemeIcon(theme.id);
  const [imageError, setImageError] = useState(false);

  // Reset image error when theme changes
  useEffect(() => {
    setImageError(false);
  }, [theme.id, imageUrl]);

  return (
    <button
      onClick={onSelect}
      role="listitem"
      type="button"
      className={`theme-gallery-card${isActive ? ' theme-gallery-card--active' : ''}${isOfficial ? ' theme-gallery-card--official' : ''}`}
      aria-label={`Select ${theme.name} theme${isActive ? ' (currently active)' : ''}${isOfficial ? ' (official theme)' : ''}`}
      aria-pressed={isActive}
      style={{
        borderRadius: 12,
        padding: 16,
        cursor: 'pointer',
        transition: 'transform var(--dur-fast, 120ms) var(--ease-standard, cubic-bezier(0.4, 0, 0.2, 1)), border-color var(--dur-fast, 120ms), box-shadow var(--dur-fast, 120ms)',
        textAlign: 'left',
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        outline: 'none',
      }}
      onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
        if (!isActive) {
          e.currentTarget.style.borderColor = 'var(--main-accent)';
          e.currentTarget.style.transform = 'translateY(-2px)';
        }
      }}
      onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
        if (!isActive) {
          e.currentTarget.style.borderColor = '';
          e.currentTarget.style.transform = 'translateY(0)';
        }
      }}
      // 🎯 WCAG 2.4.7: Visible focus indicator for keyboard navigation
      onFocus={(e: React.FocusEvent<HTMLButtonElement>) => {
        e.currentTarget.style.outline = '2px solid var(--quantum-violet, var(--main-accent))';
        e.currentTarget.style.outlineOffset = '2px';
        e.currentTarget.style.boxShadow = '0 0 0 4px color-mix(in srgb, var(--quantum-violet, var(--main-accent)) 25%, transparent)';
      }}
      onBlur={(e: React.FocusEvent<HTMLButtonElement>) => {
        e.currentTarget.style.outline = 'none';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {isOfficial && (
        <span className="theme-gallery-card__official-badge">
          OFFICIAL
        </span>
      )}
      
      <div className="theme-gallery-card__icon">
        {imageUrl && !imageError ? (
          <img 
            src={imageUrl} 
            alt=""
            aria-hidden
            loading="lazy"
            onError={() => setImageError(true)}
          />
        ) : (
          <span aria-hidden="true">{icon}</span>
        )}
      </div>
      <div className="theme-gallery-card__title">{theme.name}</div>
      <div
        className="theme-gallery-card__desc"
        title={theme.description}
      >
        {theme.description}
      </div>
      
      {isActive && (
        <span className="theme-gallery-card__active-pill">
          ✓ Active
        </span>
      )}
    </button>
  );
}

/**
 * Compact Theme Switcher (for footer/header)
 * Shows current theme with quick access to gallery
 * 
 * AUTHORIZATION PROTECTED:
 * - Only shows if user has 'change-theme' permission
 * - Resource: 'experience:theme-picker'
 * - Action: 'change-theme'
 */
export function CompactThemeSwitcher({ showLabel = true }: { showLabel?: boolean }) {
  const { theme } = useTheme();
  const [showGallery, setShowGallery] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  // Check if user can change themes (PDP policy-controlled)
  const themeAuth = useAuthorization('experience:theme-picker', 'change-theme', true);
  
  // Compute these before any early returns (hooks must be called unconditionally)
  const imageUrl = getThemeImageUrl(theme, theme === 'empowerid');
  const icon = getThemeIcon(theme);
  const themeName = resolveThemeDisplayName(theme, []);

  // Reset image error when theme changes
  useEffect(() => {
    setImageError(false);
  }, [theme, imageUrl]);
  
  // Don't show theme switcher if not authorized (after all hooks!)
  if (!themeAuth.authorized) {
    return null;
  }

  return (
    <>
      <button
        onClick={() => setShowGallery(true)}
        aria-label="Open theme gallery"
        aria-haspopup="dialog"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: 'rgba(255,255,255,0.1)',
          border: '1px solid var(--border-color)',
          borderRadius: 8,
          padding: '8px 12px',
          color: 'inherit',
          cursor: 'pointer',
          transition: 'all 0.2s',
          fontSize: 14,
        }}
        onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
        }}
        onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
        }}
        title="Change theme"
      >
        {imageUrl && !imageError ? (
          <img 
            src={imageUrl} 
            alt={`${themeName} Logo`} 
            style={{ height: 16, width: 'auto', maxWidth: 60 }}
            loading="lazy"
            onError={() => setImageError(true)}
          />
        ) : (
          <span aria-hidden="true">{icon}</span>
        )}
        {showLabel && <span>{themeName}</span>}
        <span style={{ fontSize: 12, opacity: 0.7 }}>▾</span>
      </button>

      {showGallery && (
        <ThemeGallery onClose={() => setShowGallery(false)} />
      )}
    </>
  );
}

export default ThemeGallery;

